<script setup lang="ts">
const appStore = useAppStore()
/**
 * 首页主页面，由 Nuxt I18n 为各语言生成带前缀路由。
 * 责任划分：
 * 1) 组织首屏搜索区、功能卖点区和下方各业务模块的页面布局；
 * 2) 负责首页聚合接口的拉取与字段映射，为后续组件数据化改造提供统一入口；
 * 3) 管理首屏标签动效与搜索框可见性上报（用于顶部吸附搜索联动）。
 */

// ===== 视觉与模块组件 =====
import SearchNormalIcon from '~/assets/icon/search-normal.svg'
import Radar from '~/assets/icon/radar.svg'
import Note from '~/assets/icon/note.svg'
import Alarm from '~/assets/icon/alarm.svg'
import {
  getWebHomeSurveyFeedbackMarketTrading,
  type WebHomeExchangeRankingCategory,
  type WebHomeSurveyFeedbackMarketTradingApiResponse,
  type WebHomeSurveyFeedbackMarketTradingResponse,
} from '~/server/home'
import type { AxiosResponse } from 'axios'

const HIGH_RISK_EXPOSURE_ALERT_CODE = '82300010'
definePageMeta({
  name: 'home',
  title: 'home',
})

const { localeMeta, localePath, t, locale } = useAppI18n()
useSeoMeta({
  title: t('84391'),
  ogTitle: t('84391'),
  description: t('84389'),
  ogDescription: t('84389'),
})
useHead({
  script: [
    {
      tagPosition: 'head',
    },
  ],
})
// ===== 搜索区逻辑（从 composable 复用） =====
const searchCtx = useHomeSearch()
const matomo = useMatomo()

await searchCtx.searchDefaultAsyncData

const {
  searchQuery,
  isFocused,
  hasSearched,
  searchHistory,
  popularTraders,
  traderTags,
  handleFocus,
  handleSearchInput,
  handleSearch,
  onBlur,
  selectHistoryItem,
  selectTraderItem,
  deleteHistoryItem,
  clearAllHistory,
  initSearchHistory,
} = searchCtx

// ===== 首页聚合接口字段映射：按 web_home.api 中的顶层模块一一对应 =====
// 模块渲染顺序（由后端返回，前端可按需作为动态编排依据）
const groups = ref<string[]>([])
// 左侧交易所排行榜模块
const leftExchangeRanking = ref<WebHomeSurveyFeedbackMarketTradingResponse['rankingList']>([])
// 右侧行业资讯模块
const industryNews = ref<WebHomeSurveyFeedbackMarketTradingResponse['industryNews']>([])
// 右侧高风险曝光预警模块
const highRiskExposureAlert = ref<WebHomeSurveyFeedbackMarketTradingResponse['highRiskExposureAlert']>({})
// 天眼实勘模块
const skyEyeSurvey = ref<WebHomeSurveyFeedbackMarketTradingResponse['skyEyeSurvey']>({})
// 用户反馈模块
const feedback = ref<WebHomeSurveyFeedbackMarketTradingResponse['feedback']>({})
// 加密市场数据模块
const cryptoMarketData = ref<WebHomeSurveyFeedbackMarketTradingResponse['cryptoMarketData']>({})
// 交易量排行榜模块
const cryptoTradingVolumeRankings = ref<WebHomeSurveyFeedbackMarketTradingResponse['cryptoTradingVolumeRankings']>({})

/**
 * 统一归一化接口响应结构：
 * - 兼容 `{ data: {...} }` 包裹结构；
 * - 兼容直接返回业务对象的结构；
 * - 异常值兜底为空对象，避免后续字段解构报错。
 */
const normalizeWebHomePayload = (
  payload:
    | WebHomeSurveyFeedbackMarketTradingApiResponse
    | AxiosResponse<WebHomeSurveyFeedbackMarketTradingApiResponse>
    | null,
): WebHomeSurveyFeedbackMarketTradingResponse => {
  if (!payload || typeof payload !== 'object') {
    return {}
  }

  const apiPayload = 'status' in payload ? payload.data : payload

  if ('data' in apiPayload && apiPayload.data && typeof apiPayload.data === 'object') {
    return apiPayload.data
  }

  return apiPayload as WebHomeSurveyFeedbackMarketTradingResponse
}

/**
 * 首页聚合数据拉取（SSR 首屏 + 客户端路由切换均可用），并完成字段逐一映射。
 * 当前页面先完成“数据接入与归位”，后续各业务组件可逐步改为 props 驱动。
 */
const { data: homeData } = await useAsyncData('web-home-survey-feedback-market-trading', async () => {
  // 1) 拉取首页聚合接口原始响应（双端兼容：SSR / CSR 分支由 server/home 内部处理）
  const response = await getWebHomeSurveyFeedbackMarketTrading()

  // 2) 统一响应结构：
  //    - 兼容 { data: {...} } 包裹结构
  //    - 兼容直接返回业务对象结构
  //    目标：保证后续字段读取始终基于同一个对象形态
  const normalized = normalizeWebHomePayload(response)

  // 3) 返回标准化结果给 useAsyncData 缓存，供 Nuxt payload 复用
  return normalized
})

// 基于 useAsyncData 的最终值做映射，避免仅依赖 handler 内 side-effect 导致 SSR/CSR 首帧不一致
watchEffect(() => {
  const normalized = homeData.value ?? {}
  const validRankingList = (normalized.rankingList ?? []).filter(
    (item): item is WebHomeExchangeRankingCategory => Array.isArray(item.list) && item.list.length > 0,
  )
  const highRiskRanking = validRankingList.find((item) => item.code === HIGH_RISK_EXPOSURE_ALERT_CODE)

  groups.value = normalized.groups ?? []
  leftExchangeRanking.value = validRankingList.filter((item) => item.code !== HIGH_RISK_EXPOSURE_ALERT_CODE)
  industryNews.value = normalized.industryNews ?? []
  highRiskExposureAlert.value = highRiskRanking ?? normalized.highRiskExposureAlert ?? {}
  skyEyeSurvey.value = normalized.skyEyeSurvey ?? {}
  feedback.value = normalized.feedback ?? {}
  cryptoMarketData.value = normalized.cryptoMarketData ?? {}
  cryptoTradingVolumeRankings.value = normalized.cryptoTradingVolumeRankings ?? {}
})

// ===== 首屏特性卡片：数据驱动渲染，便于未来多语言或接口化 =====
const features = [
  {
    icon: SearchNormalIcon,
    iconClass: 'h-[40px] w-[40px]',
    title: t('82870'),
    desc: t('83979', ['20,000+']),
  },
  {
    icon: Radar,
    iconClass: 'h-[40px] w-[40px]',
    title: t('82872'),
    desc: t('83980'),
  },
  {
    icon: Note,
    iconClass: 'h-[40px] w-[40px]',
    title: t('82874'),
    desc: t('83981', ['60+']),
  },
  {
    icon: Alarm,
    iconClass: 'h-[40px] w-[40px]',
    title: t('82880'),
    desc: t('83982'),
  },
]

// ===== 热门交易商标签动效状态 =====
// 直接在模板中按 index * 54px 计算偏移量，无需依赖 JS DOM 获取

// ===== 搜索区域可见性监听 =====
const { setVisible } = useSearchBoxVisibility()
// 首屏搜索区顶部距离阈值：滚动达到该距离后展示顶部吸附搜索区
const SEARCH_BOX_VISIBLE_TOP_OFFSET = 502

function syncSearchBoxVisibility() {
  setVisible(window.scrollY < SEARCH_BOX_VISIBLE_TOP_OFFSET)
}

const homeSearch = () => {
  matomo.trackEvent('home_search', {
    af_source: '首页',
  })
  matomo.trackEvent('search', {
    af_source: '首页',
  })
}
const homeSearchBroker = (item: any) => {
  matomo.trackEvent('home_search_broker', {
    af_name: item.label,
    af_code: item.code,
  })
}

onMounted(() => {
  // matomo.trackEvent('home_details', {})
  // 初始化本地搜索历史（仅客户端）
  initSearchHistory()

  // 按页面顶部滚动距离联动顶部搜索条显隐
  syncSearchBoxVisibility()
  window.addEventListener('scroll', syncSearchBoxVisibility, { passive: true })
})

onUnmounted(() => {
  // 页面卸载时释放滚动监听并重置可见状态，避免影响其他页面
  window.removeEventListener('scroll', syncSearchBoxVisibility)
  setVisible(true)
})
</script>

<template>
  <!-- 首页内容总容器 -->
  <div class="content-wrap">
    <!-- 首屏：背景图 + 标题 + 搜索 + 热门标签 + 功能卖点 -->
    <div class="search-box">
      <!-- 不要改为公共组件,设计要求加载失败显示白色的就可以了 -->
      <img
        src="https://resources1.interface003.com/web30/2026/05/19/4e31f722-7dcd-49e8-a8b4-6b73eb598100.png"
        class="search-bg-img"
        alt=""
        srcset=""
        loading="eager"
      />
      <div class="title">{{ t('83906') }}</div>
      <div class="sub-title">{{ t('83907') }}</div>
      <!-- 搜索输入与下拉建议 -->
      <div class="search-input-container" :style="[].includes(locale) ? { top: '405px' } : {}">
        <div class="search-input-wrap" :class="{ 'is-active': searchQuery }" @click="homeSearch">
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="t('80360')"
            class="search-input mr-[20px] rtl:mr-0 rtl:ml-[20px]"
            @focus="handleFocus"
            @blur="onBlur"
            @input="handleSearchInput"
            @keydown.enter.prevent="handleSearch"
          />
          <button type="button" class="search-btn" @click="handleSearch" @mousedown.prevent>{{ t('82927') }}</button>
        </div>
        <!-- 搜索下拉面板：历史记录 + 热门交易商 -->
        <!-- v-if="isFocused" -->

        <HomeSearchDropdown
          :search-query="searchQuery"
          :has-searched="hasSearched"
          v-if="isFocused"
          :search-history="searchHistory"
          :popular-traders="popularTraders"
          @select-history="selectHistoryItem"
          @select-trader="selectTraderItem"
          @delete-history="deleteHistoryItem"
          @clear-history="clearAllHistory"
        />
      </div>
      <!-- 热门交易商标签条：纯 CSS 控制动画，保证首屏时序完美同步 -->
      <div class="traders-tags-wrap" :style="[].includes(locale) ? { top: '503px' } : {}">
        <NuxtLink
          v-for="(item, index) in traderTags"
          :key="index"
          class="tag-item animate-expand"
          :to="localePath({ name: 'exchange-detail', params: { slug: item.alias } })"
          @click="homeSearchBroker(item)"
          target="_blank"
          :style="{
            '--slide-offset': `-${index * 54}px`,
          }"
        >
          <CommonImage :src="item.icon" alt="" class="tag-icon" loading="eager" object-fit="cover" />
          <span class="tag-label">{{ item.label }}</span>
        </NuxtLink>
      </div>
      <!-- 平台核心优势区 -->
      <div class="features-wrap" :style="[].includes(locale) ? { marginTop: '-28px' } : {}">
        <template v-for="(feature, index) in features" :key="feature.title">
          <div class="feature-item">
            <div class="feature-icon">
              <component :is="feature.icon" :class="feature.iconClass" />
            </div>
            <div class="feature-text">
              <div class="feature-title" v-html="feature.title"></div>
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div class="feature-desc" v-html="feature.desc"></div>
            </div>
          </div>
          <div v-if="index < features.length - 1" class="feature-divider"></div>
        </template>
      </div>
    </div>

    <!-- 主体第一屏：左侧交易所排行 + 右侧资讯/预警 -->
    <div class="home-main-grid">
      <HomeExchange :data="leftExchangeRanking" />
      <div class="home-sidebar-grid">
        <HomeIndustryNews v-if="industryNews && industryNews.length > 0" :data="industryNews" />
        <LazyVisibleHomeHighRiskExposureAlert
          :hydrate-on-visible="{ rootMargin: `${appStore.hydrateOnVisibleRootMargin}px 0px` }"
          :data="highRiskExposureAlert"
        />
      </div>
    </div>
    <HomeMarketTicker />

    <!-- 天眼实勘模块 -->
    <div v-if="Array.isArray(skyEyeSurvey?.cards) && skyEyeSurvey.cards.length > 0" class="w-full bg-[#112D73]">
      <div class="w-full">
        <LazyVisibleHomeExposure
          :hydrate-on-visible="{ rootMargin: `${appStore.hydrateOnVisibleRootMargin}px 0px` }"
          :data="skyEyeSurvey"
        />
      </div>
    </div>

    <!-- 用户反馈模块 -->
    <div v-if="Array.isArray(feedback?.cards) && feedback.cards.length > 0" class="w-full">
      <div class="w-full">
        <LazyVisibleHomeFeedback
          :hydrate-on-visible="{ rootMargin: `${appStore.hydrateOnVisibleRootMargin}px 0px` }"
          :data="feedback"
        />
      </div>
    </div>

    <!-- 市场数据模块 -->
    <div class="w-full bg-[#f8f8f8]">
      <div class="w-full">
        <LazyVisibleHomeMarket
          :hydrate-on-visible="{ rootMargin: `${appStore.hydrateOnVisibleRootMargin}px 0px` }"
          :data="cryptoMarketData"
        />
      </div>
    </div>

    <!-- 交易量排行榜模块 -->
    <div
      v-if="Array.isArray(cryptoTradingVolumeRankings?.rows) && cryptoTradingVolumeRankings.rows.length > 0"
      class="w-full bg-[#f8f8f8]"
    >
      <div class="w-full">
        <LazyVisibleHomeTrading
          :hydrate-on-visible="{ rootMargin: `${appStore.hydrateOnVisibleRootMargin}px 0px` }"
          :data="cryptoTradingVolumeRankings"
        />
      </div>
    </div>

    <!-- 下载/落地引导模块 -->
    <div class="w-full">
      <LazyVisibleHomeDown :hydrate-on-visible="{ rootMargin: `${appStore.hydrateOnVisibleRootMargin}px 0px` }" />
    </div>
  </div>
</template>

<style scoped lang="less">
/* ===== 默认布局（Desktop First）===== */
.content-wrap {
  overflow-x: clip;
  /* 首屏区域：包含背景、标题、搜索、标签、特色卡片 */
  .search-box {
    position: relative;
    overflow-x: clip;

    .title {
      position: absolute;
      top: 125px;
      left: 0;
      right: 0;
      text-align: center;
      color: #292a2e;
      font-family: Outfit;
      font-size: 62px;
      font-style: normal;
      font-weight: 700;
      line-height: normal;
      animation: fadeInUp 0.8s cubic-bezier(0.25, 1, 0.5, 1) both;
    }

    /* 次标题：主标题之后延时进入，形成层次感 */
    .sub-title {
      position: absolute;
      color: #112d73;
      top: 210px;
      left: 0;
      right: 0;
      text-align: center;
      font-family: Outfit;
      font-size: 62px;
      font-style: normal;
      font-weight: 700;
      line-height: normal;
      animation: fadeInUp 0.8s cubic-bezier(0.25, 1, 0.5, 1) 0.4s both;
    }

    .search-bg-img {
      width: 1500px;
      height: 654px;
      object-fit: cover;
      position: relative;
      left: 50%;
      transform: translateX(-50%);
    }

    /* 搜索区容器：绝对定位到首屏中部 */
    .search-input-container {
      position: absolute;
      top: 327px;
      left: 0;
      right: 0;
      display: flex;
      justify-content: center;

      /* 搜索输入外层：统一控制 hover / focus / active / filled 状态 */
      .search-input-wrap {
        width: 816px;
        height: 66px;
        background-color: #ffffff;
        border-radius: 32px;
        border: 1px solid rgba(5, 21, 36, 0.08);
        box-shadow: 0 8px 24px rgba(5, 21, 36, 0.04);
        display: flex;
        align-items: center;
        padding: 0 8px 0 32px;
        padding-inline-start: 32px;
        padding-inline-end: 8px;
        box-sizing: border-box;
        opacity: 0;
        animation: searchBoxFadeIn 3s cubic-bezier(0.25, 1, 0.5, 1) 0.8s both;
        transition:
          background-color 0.25s ease,
          border-color 0.25s ease,
          box-shadow 0.25s ease;

        &:hover {
          background-color: #f8f8f8;
          border-color: rgba(5, 21, 36, 0.08);
        }

        &:active {
          background-color: #eaecef;
        }

        &:focus-within {
          background-color: #ffffff;
          border: 2px solid rgba(17, 45, 115, 0.3);
          box-shadow: 0 8px 24px rgba(17, 45, 115, 0.06);

          &:hover {
            background-color: #ffffff;
          }
        }

        &.is-active {
          background-color: #ffffff;
          border: 1px solid #112d73;
          box-shadow: 0 8px 24px rgba(17, 45, 115, 0.08);

          &:hover {
            background-color: #ffffff;
          }
        }

        .search-input {
          flex: 1;
          height: 100%;
          min-width: 0;
          border: none;
          outline: none;
          font-family: Outfit;
          font-size: 16px;
          color: #292a2e;
          background: transparent;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;

          &::placeholder {
            color: #7d818a;
            font-family: PingFang SC;
            font-size: 16px;
            font-style: normal;
            font-weight: 400;
          }
        }

        /* 右侧搜索按钮 */
        .search-btn {
          height: 50px;
          min-width: 128px;
          padding: 0 36px;
          background: var(--color-brand-default, #112d73);
          color: #ffffff;
          border: none;
          border-radius: 26px;
          font-family: Outfit;
          font-size: 18px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition:
            background 0.2s ease,
            transform 0.1s ease;
          &:hover {
            background:
              linear-gradient(0deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.08) 100%),
              var(--color-brand-default, #112d73);
          }
          &:active {
            background:
              linear-gradient(0deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.1) 100%), var(--color-brand-default, #112d73);
            transform: scale(0.98);
          }
        }
      }
    }

    /* 交易商标签条：默认胶囊态，hover 后展开文字 */
    .traders-tags-wrap {
      display: flex;
      align-items: center;
      position: absolute;
      top: 425px;
      left: 50%;
      transform: translateX(-50%);
      gap: 12px;

      .tag-item {
        min-width: 42px;
        max-width: 42px;
        height: 42px;
        border-radius: 21px;
        background: #fff;
        border: 0.5px solid var(--color-border-subtle, rgba(11, 18, 40, 0.14));
        padding: 0 10px;
        justify-content: flex-start;
        align-items: center;
        display: flex;
        cursor: pointer;
        opacity: 0;
        overflow: hidden;
        box-sizing: border-box;
        transition:
          max-width 0.4s cubic-bezier(0.16, 1, 0.3, 1),
          padding 0.4s cubic-bezier(0.16, 1, 0.3, 1),
          background-color 0.25s ease,
          border-color 0.25s ease,
          box-shadow 0.25s ease;

        &.animate-expand {
          animation: expandSlide 0.8s cubic-bezier(0.25, 1, 0.5, 1) 1s both;
        }

        .tag-icon {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          object-fit: cover;
          flex-shrink: 0;
        }

        .tag-label {
          font-family: Outfit;
          font-size: 14px;
          font-weight: 500;
          color: #292a2e;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 0;
          opacity: 0;
          transition:
            max-width 0.4s cubic-bezier(0.16, 1, 0.3, 1),
            opacity 0.25s ease,
            margin-inline-start 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          margin-inline-start: 0;
        }

        &:hover {
          max-width: 200px;
          padding: 0 12px;
          background: var(--color-background-default, #fff);
          border: 0.5px solid var(--color-border-subtle, rgba(11, 18, 40, 0.14));
          box-shadow: 0 8px 24px rgba(11, 18, 40, 0.08);

          .tag-label {
            max-width: 140px;
            opacity: 1;
            margin-inline-start: 8px;
          }
        }
      }
    }

    /* 平台特性卡片区：支持分卡延时入场 */
    .features-wrap {
      position: relative;
      margin-top: -106px;
      margin-inline: auto;
      z-index: 10;
      width: 1200px;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      box-sizing: border-box;
      opacity: 0;
      animation: fadeInUpFeature 0.8s cubic-bezier(0.25, 1, 0.5, 1) 1.2s both;

      .feature-item {
        display: flex;
        align-items: center;
        gap: 16px;
        width: 270px;
        opacity: 0;
        animation: featureCardFadeIn 0.8s cubic-bezier(0.25, 1, 0.5, 1) both;
        transition: transform 0.5s cubic-bezier(0.25, 1, 0.5, 1);

        &:hover {
          transform: translateY(-4px) !important;
          cursor: pointer;
        }

        &:nth-child(1) {
          animation-delay: 1.2s;
        }
        &:nth-child(3) {
          animation-delay: 1.3s;
        }
        &:nth-child(5) {
          animation-delay: 1.4s;
        }
        &:nth-child(7) {
          animation-delay: 1.5s;
        }

        .feature-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          width: 50px;
          height: 50px;
          .feature-icon-img {
            width: 32px;
            height: 32px;
          }
        }

        .feature-text {
          display: flex;
          flex-direction: column;
          gap: 4px;

          .feature-title {
            color: #292a2e;
            font-family: Outfit, sans-serif;
            font-size: 16px;
            font-weight: 500;
          }

          .feature-desc {
            color: #7d818a;
            font-family: Outfit, sans-serif;
            font-size: 12px;
            font-weight: 400;
            text-align: start;
          }
        }
      }

      .feature-divider {
        width: 1px;
        height: auto;
        min-height: 54px;
        align-self: stretch;
        margin-top: 10px;
        margin-bottom: 10px;
        background-color: rgba(11, 18, 40, 0.14);
        flex-shrink: 0;
      }
    }
  }

  /* 主内容首行：左主右辅两列布局 */
  .home-main-grid {
    width: 1200px;
    margin: 80px auto 80px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 348px;
    column-gap: 32px;
    align-items: start;
  }

  /* 右侧栏：资讯与预警纵向堆叠 */
  .home-sidebar-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    row-gap: 20px;
  }
}

/* ===== 平板布局（800-1200）===== */
@media screen and (min-width: 800px) and (max-width: 1200px) {
  .content-wrap {
    overflow-x: clip;

    .search-box {
      .title,
      .sub-title {
        font-size: 48px;
      }
      .search-input-container {
        padding: 0 24px;
        box-sizing: border-box;

        .search-input-wrap {
          width: 100%;
          max-width: 816px;
        }

        :deep(.search-dropdown-wrap) {
          width: calc(100% - 48px);
          max-width: 816px;
        }
      }

      .traders-tags-wrap {
        width: calc(100% - 48px);
        justify-content: center;
        flex-wrap: wrap;
      }

      .features-wrap {
        width: calc(100% - 48px);
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 24px 32px;
        align-items: start;

        .feature-item {
          width: 100%;
        }

        .feature-divider {
          display: none;
        }
      }
    }

    .home-main-grid {
      width: calc(100% - 48px);
      grid-template-columns: minmax(0, 1fr);
      row-gap: 20px;
    }
  }
}

/* ===== 小屏布局（<=799）===== */
@media screen and (max-width: 799px) {
  .content-wrap {
    overflow-x: clip;

    .search-box {
      .title,
      .sub-title {
        font-size: 48px;
      }

      .search-input-container {
        padding: 0 24px;
        box-sizing: border-box;

        .search-input-wrap {
          width: 816px;
          max-width: 816px;
        }

        :deep(.search-dropdown-wrap) {
          width: 768px;
          max-width: 768px;
        }
      }

      .traders-tags-wrap {
        width: calc(816px - 48px);
        justify-content: center;
        flex-wrap: wrap;
      }

      .features-wrap {
        width: calc(816px - 48px);
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 24px 32px;
        align-items: start;

        .feature-item {
          width: 816px;
        }

        .feature-divider {
          display: none;
        }
      }
    }

    .home-main-grid {
      width: calc(816px - 48px);
      grid-template-columns: minmax(0, 1fr);
      row-gap: 20px;
    }
  }
}

/* 标题/副标题淡入上移 */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(15px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 标签从左侧位移进入并放大到正常尺寸 */
@keyframes expandSlide {
  0% {
    opacity: 0;
    transform: translateX(var(--slide-offset, 0px)) scale(0.8);
  }
  100% {
    opacity: 1;
    transform: translateX(0px) scale(1);
  }
}

/* 搜索框入场动画（带轻微回弹） */
@keyframes searchBoxFadeIn {
  0% {
    opacity: 0;
    transform: translateY(15px) scale(1);
  }
  70% {
    transform: translateY(0) scale(1.03);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* 特性区整体淡入 */
@keyframes fadeInUpFeature {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

/* 特性卡片逐个上移显现 */
@keyframes featureCardFadeIn {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
html[dir='rtl'] {
  .search-input-wrap {
    padding: 0 32px 0 6px;
    padding-inline-start: 32px;
    padding-inline-end: 6px;
  }

  .tag-item {
    &.animate-expand {
      animation-name: expandSlideRtl;
    }
  }

  .search-bg-img {
    right: 50%;
    transform: translateX(50%) !important;
  }
}

/* RTL 下的标签入场动画：位移方向与 LTR 相反 */
@keyframes expandSlideRtl {
  0% {
    opacity: 0;
    transform: translateX(calc(-1 * var(--slide-offset, 0px))) scale(0.8);
  }
  100% {
    opacity: 1;
    transform: translateX(0px) scale(1);
  }
}
</style>
